import { AuthGuardMode } from '~/types/authGuard';

export default defineAppConfig({
    authGuard: {
        // Configurations for authGuard middleware
        redirect: {
            [AuthGuardMode.AUTHENTICATED_ONLY]: 'index',
            [AuthGuardMode.GUEST_ONLY]: 'index',
        },
    },
    ui: {
        colors: {
            primary: 'fl-blue',
            secondary: 'fl-yellow',
            neutral: 'slate',
        },
        icons: {
            loading: 'i-svg-spinners-270-ring-with-bg',
            file: 'lucide:cloud-upload',
        },
        accordion: {
            slots: {
                item: 'border-neutral-200',
            },
        },
        badge: {
            variants: {
                color: {
                    'primary-dark': '',
                },
            },
            compoundVariants: [
                {
                    class: 'rounded-full',
                },
                {
                    color: 'primary-dark',
                    variant: 'solid',
                    class: 'bg-primary-950 text-inverted',
                },
            ],
        },
        breadcrumb: {
            slots: {
                link: 'focus-visible:outline-inherit',
            },
            variants: {
                active: {
                    true: {
                        link: 'text-muted font-normal',
                    },
                    false: {
                        link: 'text-muted font-normal',
                    },
                },
            },
        },
        button: {
            slots: {
                base: 'rounded-full',
            },
            variants: {
                color: {
                    'primary-dark': '',
                },
            },
            compoundVariants: [
                {
                    color: 'primary-dark',
                    variant: 'solid',
                    class: 'text-inverted bg-primary-950 hover:bg-primary-950/80 active:bg-primary-950 disabled:bg-primary-950/75 aria-disabled:bg-primary-950/75 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-950',
                },
                {
                    color: 'primary-dark',
                    variant: 'link',
                    class: 'text-primary-950 hover:text-primary-950/80 active:text-primary-950 disabled:text-primary-950/75 aria-disabled:text-primary-950/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-950',
                },
                {
                    color: 'secondary',
                    variant: 'solid',
                    class: 'text-highlighted bg-secondary hover:bg-secondary/75 active:bg-secondary/75 disabled:bg-secondary-50 aria-disabled:bg-secondary-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary',
                },
            ],
        },
        card: {
            slots: {
                root: 'rounded-xl',
                body: 'flex flex-col gap-3',
            },
        },
        checkbox: {
            slots: {
                base: 'cursor-pointer',
            },
        },
        formField: {
            variants: {
                size: {
                    xl: {
                        root: 'text-sm',
                    },
                },
            },
            defaultVariants: {
                size: 'xl',
            },
        },
        input: {
            slots: {
                root: 'w-full',
            },
            defaultVariants: {
                size: 'xl',
            },
        },
        modal: {
            slots: {
                overlay: 'bg-neutral-200/35',
                title: 'text-xl font-bold md:text-2xl',
                description: 'text-sm md:text-base',
                content: 'divide-y-0',
                body: 'scrollbar-hide',
                footer: 'flex-col',
            },
        },
        navigationMenu: {
            variants: {
                orientation: {
                    horizontal: {
                        link: 'text-base',
                    },
                },
            },
        },
        pagination: {
            slots: {
                list: 'justify-center',
            },
        },
        popover: {
            slots: {
                content: 'bg-primary-950 text-inverted',
            },
        },
        selectMenu: {
            slots: {
                base: 'w-full',
                value: 'max-w-full self-start',
                placeholder: 'self-start',
                content: 'min-w-fit',
            },
            defaultVariants: {
                size: 'xl',
            },
        },
        separator: {
            variants: {
                neutral: { border: 'border-neutral-300' },
            },
        },
        textarea: {
            slots: {
                root: 'w-full',
            },
            defaultVariants: {
                size: 'xl',
            },
        },
        tooltip: {
            slots: {
                content: 'bg-primary-950 text-inverted',
                arrow: 'fill-primary-950',
            },
        },
    },
    // Fix build error "Property 'nuxtIcon' is missing in type 'AppConfig'" (https://github.com/nuxt/ui/issues/1045)
    nuxtIcon: {},
    webSocket: {
        autoReconnect: {
            retries: 12 * 60 * 24 * 7, // 1 week
            delay: 5000,
        },
        heartbeat: {
            interval: 60 * 1000, // 1 minute
            pongTimeout: 3000,
        },
    },
});
